import type { ReactNode } from 'react';
import type { Client, ClientPeriodBill } from '@core/generated/gql-types';
import type { PayRunCycleLogWithReferences } from '@core/payroll/payroll.type';
import Button from '@/components/Button';
import PayrollStatusPill from '@/modules/payroll/components/shared/PayrollStatusPill';
import {
  buildCycleClientsTable,
  buildCycleDaySequencesTable,
  buildCycleJobsTable,
  buildCycleWorkersTable,
  getCycleCoverage,
  getCyclePeriodLengthDays,
  getIntegrationSyncSummaries,
  type IntegrationSyncSummary,
  type PayrollTableData,
} from '@/modules/payroll/utils/payRunCycle.utils';
import {
  formatCents,
  formatDateRangeLabel,
  formatDateTimeLabel,
  formatEnumLabel,
} from '@/modules/payroll/utils/payrollFormat.utils';

const PREVIEW_COUNT = 2;

const DetailCard = ({
  title,
  wide = false,
  children,
}: {
  title: string;
  wide?: boolean;
  children: ReactNode;
}) => (
  <section
    className={`flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-3 ${
      wide ? 'md:col-span-3' : ''
    }`}
  >
    <h4 className="text-base font-semibold text-gray-900">{title}</h4>
    {children}
  </section>
);

const ActionLink = ({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
  >
    {children}
  </button>
);

type CycleLogDetailProps = {
  cycle: PayRunCycleLogWithReferences;
  isLoadingDetails: boolean;
  detailsError: string | null;
  clientBillsById: Record<string, ClientPeriodBill>;
  billClientsById: Record<string, Client>;
  isLoadingBills: boolean;
  isForceSyncing: boolean;
  showForceSync: boolean;
  onForceSync: () => void;
  onOpenTable: (table: PayrollTableData) => void;
  onOpenIntegrationFile: (
    summary: IntegrationSyncSummary,
    file: 'report' | 'payload',
  ) => void;
};

const CycleLogDetail = ({
  cycle,
  isLoadingDetails,
  detailsError,
  clientBillsById,
  billClientsById,
  isLoadingBills,
  isForceSyncing,
  showForceSync,
  onForceSync,
  onOpenTable,
  onOpenIntegrationFile,
}: CycleLogDetailProps) => {
  const coverage = getCycleCoverage(cycle);
  const marginCents =
    (cycle.totalClientBillableAmountCents ?? 0) -
    (cycle.totalPayableAmountCents ?? 0);
  const integrations = getIntegrationSyncSummaries(cycle.externalReferences);
  const hasPayRunItems = coverage.daySequenceCount > 0;

  const openSummaryTable = (title: string, entries: string[]) =>
    onOpenTable({
      title,
      subtitle: `${entries.length} entries`,
      columns: ['Entry'],
      rows: entries.map((entry) => [entry]),
    });

  const openClientBillsTable = () =>
    onOpenTable({
      title: 'Client bills',
      subtitle: `${cycle.clientBillIds.length} bills`,
      columns: ['Bill ID', 'Client', 'Status', 'Total', 'Invoice'],
      rows: cycle.clientBillIds.map((billId) => {
        const bill = clientBillsById[billId];
        if (!bill) {
          return [
            billId,
            isLoadingBills ? 'Loading…' : 'Unavailable',
            '',
            '',
            '',
          ];
        }
        return [
          bill.id,
          billClientsById[bill.clientId]?.name ?? bill.clientId,
          formatEnumLabel(bill.status),
          formatCents(bill.totalBillableCents ?? 0),
          bill.invoiceUrl ? 'Yes' : 'No',
        ];
      }),
    });

  const withCycleTitle = (table: PayrollTableData): PayrollTableData => ({
    ...table,
    title: `${table.title} · ${formatDateRangeLabel(
      cycle.payRunDateFromISO,
      cycle.payRunDateToISO,
    )}`,
  });

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <DetailCard title="Cycle snapshot" wide>
        <p className="text-sm text-slate-700">
          {formatDateRangeLabel(cycle.periodStartISO, cycle.periodEndISO)} ·{' '}
          {getCyclePeriodLengthDays(cycle) || '—'} days
        </p>
        <p className="text-sm text-slate-700">
          Run at {formatDateTimeLabel(cycle.createdAt)}
        </p>
        <div>
          <PayrollStatusPill status={cycle.status} />
        </div>
      </DetailCard>

      <DetailCard title="Money">
        <p className="text-sm text-slate-700">
          Payable {formatCents(cycle.totalPayableAmountCents)}
        </p>
        <p className="text-sm text-slate-700">
          Billable {formatCents(cycle.totalClientBillableAmountCents)}
        </p>
        <p className="text-lg font-semibold text-gray-900">
          Margin {formatCents(marginCents)}
        </p>
      </DetailCard>

      <DetailCard title="Coverage">
        <p className="text-sm text-slate-700">Workers {coverage.workerCount}</p>
        <p className="text-sm text-slate-700">Clients {coverage.clientCount}</p>
        <p className="text-sm text-slate-700">Jobs {coverage.jobCount}</p>
        <p className="text-sm text-slate-700">
          Day sequences {coverage.daySequenceCount}
        </p>
      </DetailCard>

      <DetailCard title="Exceptions">
        <p className="text-sm text-slate-700">
          Errors {cycle.errorSummary.length}
        </p>
        {cycle.errorSummary.slice(0, PREVIEW_COUNT).map((entry, index) => (
          <p
            key={`error-${index}`}
            className="text-xs break-words text-red-700"
          >
            {entry}
          </p>
        ))}
        {cycle.errorSummary.length > PREVIEW_COUNT && (
          <button
            type="button"
            className="self-start text-xs text-[#03406A] hover:underline"
            onClick={() => openSummaryTable('Errors', cycle.errorSummary)}
          >
            View all errors
          </button>
        )}
        <p className="text-sm text-slate-700">
          Auto-fixes {cycle.autoFixedItemsSummary.length}
        </p>
        {cycle.autoFixedItemsSummary
          .slice(0, PREVIEW_COUNT)
          .map((entry, index) => (
            <p
              key={`autofix-${index}`}
              className="text-xs break-words text-slate-600"
            >
              {entry}
            </p>
          ))}
        {cycle.autoFixedItemsSummary.length > PREVIEW_COUNT && (
          <button
            type="button"
            className="self-start text-xs text-[#03406A] hover:underline"
            onClick={() =>
              openSummaryTable('Auto-fixes', cycle.autoFixedItemsSummary)
            }
          >
            View all auto-fixes
          </button>
        )}
      </DetailCard>

      <DetailCard title="Actions" wide>
        <div className="flex flex-wrap gap-2">
          {showForceSync && (
            <Button
              variant="outline"
              className="py-1.5 text-sm"
              onClick={onForceSync}
              isLoading={isForceSyncing}
            >
              Force sync
            </Button>
          )}
          <ActionLink
            disabled={!hasPayRunItems || isLoadingDetails}
            onClick={() =>
              onOpenTable(withCycleTitle(buildCycleDaySequencesTable(cycle)))
            }
          >
            {isLoadingDetails
              ? 'Loading day sequences…'
              : 'View all day sequences'}
          </ActionLink>
          <ActionLink
            disabled={cycle.payRuns.length === 0}
            onClick={() =>
              onOpenTable(withCycleTitle(buildCycleWorkersTable(cycle)))
            }
          >
            View workers
          </ActionLink>
          <ActionLink
            disabled={coverage.jobCount === 0}
            onClick={() =>
              onOpenTable(withCycleTitle(buildCycleJobsTable(cycle)))
            }
          >
            View jobs
          </ActionLink>
          <ActionLink
            disabled={!hasPayRunItems}
            onClick={() =>
              onOpenTable(withCycleTitle(buildCycleClientsTable(cycle)))
            }
          >
            View clients
          </ActionLink>
          {cycle.fileUrl && (
            <a
              href={cycle.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
            >
              Download export
            </a>
          )}
          {cycle.payrollSyncReportUrl && (
            <a
              href={cycle.payrollSyncReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
            >
              View sync report
            </a>
          )}
        </div>
        {detailsError && <p className="text-sm text-red-700">{detailsError}</p>}
      </DetailCard>

      {cycle.clientBillIds.length > 0 && (
        <DetailCard title="Client bills" wide>
          <p className="text-sm text-slate-700">
            {cycle.clientBillIds.length} client bill
            {cycle.clientBillIds.length === 1 ? '' : 's'}
          </p>
          <div>
            <ActionLink onClick={openClientBillsTable}>
              View client bills
            </ActionLink>
          </div>
        </DetailCard>
      )}

      <DetailCard title="Payroll sync" wide>
        {integrations.length === 0 ? (
          <p className="text-sm text-slate-500">No sync metadata available.</p>
        ) : (
          integrations.map((integration) => (
            <div
              key={integration.provider}
              className="flex flex-wrap items-start justify-between gap-3 border-t border-slate-100 pt-2 first:border-t-0 first:pt-0"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-gray-900">
                  {integration.label}
                </span>
                {integration.lastSyncedAt !== null && (
                  <span className="text-xs text-slate-600">
                    Last synced {formatDateTimeLabel(integration.lastSyncedAt)}
                  </span>
                )}
                {integration.totalsLabel && (
                  <span className="text-xs text-slate-600">
                    Totals {integration.totalsLabel}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {integration.reportUrl && (
                  <ActionLink
                    onClick={() => onOpenIntegrationFile(integration, 'report')}
                  >
                    Report
                  </ActionLink>
                )}
                {integration.payloadUrl && (
                  <ActionLink
                    onClick={() =>
                      onOpenIntegrationFile(integration, 'payload')
                    }
                  >
                    Payload
                  </ActionLink>
                )}
              </div>
            </div>
          ))
        )}
      </DetailCard>
    </div>
  );
};

export default CycleLogDetail;
